import React, { useMemo, useState } from 'react';
import _ from 'underscore';
import moment from 'moment';
import cx from 'classnames';

import {
  ActionSubjectIdType,
  Analytics,
  SourceType,
} from '@trello/atlassian-analytics';
import { getDateWithAddedMonths, isDateToday } from '@trello/dates';
import { BackIcon } from '@trello/nachos/icons/back';
import { Button } from '@trello/nachos/button';
import { ForwardIcon } from '@trello/nachos/icons/forward';

import { Feature } from 'app/scripts/debug/constants';
import {
  getDaysToRenderForMonth,
  getGenericEventsForRange,
  isInRange,
} from 'app/src/components/AdvancedCalendarView/helpers';
import { ErrorBoundary } from 'app/src/components/ErrorBoundary';

import { ViewType } from './types';

import styles from './JumpToCalendar.less';

interface JumpToCalendarProps {
  events: { start: Date; end: Date }[];
  onNavigateToDate: (date: Date) => void;
  defaultDate: Date;
  shouldNotNavigate?: (day: Date) => void;
  viewName: ViewType;
  analyticsContainers: {
    board?: { id: string | null };
    organization?: { id: string | null };
    enterprise?: { id: string | null };
  };
  className?: string;
}

export const JumpToCalendar: React.FunctionComponent<JumpToCalendarProps> = ({
  events,
  onNavigateToDate,
  defaultDate,
  shouldNotNavigate,
  viewName,
  analyticsContainers,
  className,
}) => {
  const [focusedDay, setFocusedDay] = useState(defaultDate);
  const [focusedMonth, setFocusedMonth] = useState(defaultDate);

  const [daysToRender, daysToRenderByWeek] = useMemo(() => {
    const daysToRender = getDaysToRenderForMonth(focusedMonth);
    const daysToRenderByWeek = _.chunk(daysToRender, 7) as Date[][];

    return [daysToRender, daysToRenderByWeek];
  }, [focusedMonth]);

  const eventsInRange = useMemo(() => {
    return getGenericEventsForRange(
      events,
      daysToRender[0],
      daysToRender[daysToRender.length - 1],
    );
  }, [daysToRender, events]);

  const containsEvent = (day: Date) => {
    return eventsInRange.some(({ start, end }) =>
      isInRange(
        { start, end },
        moment(day).startOf('day').toDate(),
        moment(day).endOf('day').toDate(),
      ),
    );
  };

  const handleDayClick = (day: Date) => {
    if (shouldNotNavigate && shouldNotNavigate(day)) {
      setFocusedDay(day);
    } else {
      let buttonName: ActionSubjectIdType = 'calendarJumpToDateButton';
      let source: SourceType = 'calendarViewScreen';
      if (viewName === ViewType.TIMELINE) {
        buttonName = 'timelineJumpToDateButton';
        source = 'timelineViewScreen';
      }

      Analytics.sendClickedButtonEvent({
        buttonName,
        source,
        containers: analyticsContainers,
      });

      onNavigateToDate(day);
    }
  };

  return (
    <ErrorBoundary
      tags={{
        ownershipArea: 'trello-ecosystem',
        feature: Feature.CalendarView,
      }}
    >
      <div className={cx(className)}>
        <div className={styles.header}>
          <Button
            className={styles.headerButton}
            onClick={() =>
              setFocusedMonth(getDateWithAddedMonths(focusedMonth, -1))
            }
            iconAfter={<BackIcon size="small" />}
          />
          <div className={styles.focusedDateText}>
            {moment(focusedMonth).format('MMMM YYYY')}
          </div>
          <Button
            className={styles.headerButton}
            onClick={() =>
              setFocusedMonth(getDateWithAddedMonths(focusedMonth, 1))
            }
            iconAfter={<ForwardIcon size="small" />}
          />
        </div>
        <div className={styles.dayOfWeekNames}>
          {daysToRender.slice(0, 7).map((date, index) => (
            <div className={styles.dayOfWeek} key={index}>
              {moment(date).format('ddd')}
            </div>
          ))}
        </div>
        {daysToRenderByWeek.map((week, weekIndex) => (
          <div className={styles.dateWeek} key={weekIndex}>
            {week.map((day, dayIndex) => {
              const isSelected = moment(day).isSame(focusedDay, 'day');

              return (
                <div
                  className={cx(
                    styles.dateDay,
                    focusedMonth.getMonth() !== day.getMonth() &&
                      styles.isOffRange,
                    isDateToday(day) && styles.isToday,
                    isSelected && styles.isSelected,
                  )}
                  key={dayIndex}
                  onClick={() => (isSelected ? null : handleDayClick(day))}
                  role="button"
                >
                  <div>{moment(day).format('D')}</div>
                  <div
                    className={cx(
                      styles.hasEventDot,
                      containsEvent(day) && styles.show,
                    )}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </ErrorBoundary>
  );
};
